import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { credentialsForEmbeddingProfile } from "../../helpers/knowledgeManagement";

const useStyles = makeStyles((theme) => ({
  paper: { borderRadius: 18 },
  content: { display: "flex", flexDirection: "column", gap: theme.spacing(2), minWidth: 480 },
  hint: { padding: theme.spacing(1.5), borderRadius: 10, background: theme.palette.action.hover },
}));

const KnowledgeReindexModal = ({ open, onClose, onStarted, knowledgeBase, profiles, credentials }) => {
  const classes = useStyles();
  const [profileId, setProfileId] = useState("");
  const [credentialId, setCredentialId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const target = profiles.find((profile) => profile.availableForNewUse && Number(profile.id) !== Number(knowledgeBase?.embeddingProfileId))
      || profiles.find((profile) => profile.availableForNewUse);
    const compatible = credentialsForEmbeddingProfile(credentials, target);
    setProfileId(target?.id || "");
    setCredentialId(compatible[0]?.id || "");
  }, [credentials, knowledgeBase, open, profiles]);

  const selectedProfile = useMemo(
    () => profiles.find((profile) => Number(profile.id) === Number(profileId)),
    [profileId, profiles]
  );
  const compatibleCredentials = useMemo(
    () => credentialsForEmbeddingProfile(credentials, selectedProfile),
    [credentials, selectedProfile]
  );

  const changeProfile = (event) => {
    const value = event.target.value;
    const profile = profiles.find((item) => Number(item.id) === Number(value));
    const compatible = credentialsForEmbeddingProfile(credentials, profile);
    setProfileId(value);
    setCredentialId(compatible[0]?.id || "");
  };

  const start = async () => {
    setSaving(true);
    try {
      await api.post(`/knowledge-bases/${knowledgeBase.id}/reindex`, {
        embeddingProfileId: Number(profileId),
        credentialId: Number(credentialId),
      });
      toast.success("Reindexação iniciada. A geração atual continuará em uso até a aprovação.");
      onStarted();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
      <DialogTitle>Reindexar base de conhecimento</DialogTitle>
      <DialogContent dividers>
        <div className={classes.content}>
          <Typography variant="body2" className={classes.hint}>
            Todas as fontes ativas serão processadas em uma nova geração. A versão atual permanece disponível para {knowledgeBase?.agents?.length || 0} agente(s) até a aprovação final.
          </Typography>
          <FormControl variant="outlined" required>
            <InputLabel>Novo perfil de embeddings</InputLabel>
            <Select value={profileId} onChange={changeProfile} label="Novo perfil de embeddings">
              {profiles.filter((profile) => profile.availableForNewUse).map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.code} · {profile.dimension} dimensões{profile.recommended ? " · recomendado" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" required>
            <InputLabel>Credencial de embeddings</InputLabel>
            <Select value={credentialId} onChange={(event) => setCredentialId(event.target.value)} label="Credencial de embeddings">
              {compatibleCredentials.map((credential) => (
                <MenuItem key={credential.id} value={credential.id}>
                  {credential.provider} · {credential.secretHint || "configurada"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!compatibleCredentials.length && (
            <Typography variant="body2" color="error">Não há credencial ativa compatível com o perfil selecionado.</Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={start} disabled={!profileId || !credentialId || saving}>
          {saving ? <CircularProgress size={22} /> : "Iniciar reindexação"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KnowledgeReindexModal;
